import { useEffect, useState, useMemo } from 'react';
import { getGraficaCliente, getGraficaUltimaVisitaCliente, getVisitasEjecutivos, getVisitasInplant, getVisitasTotales, getVisitasZona } from './reporteVisitasIntegracion';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, Cell,
    PieChart, Pie, ResponsiveContainer
} from 'recharts';
import './ReporteDeVisitas.scss';



const ReporteDeVisitas = () => {
    const [fechaInicio, setFechaInicio] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [fechaFin, setFechaFin] = useState(new Date());
    const [loading, setLoading] = useState(false);

    const [totales, setTotales] = useState(null);
    const [graficaCliente, setGraficaCliente] = useState([]);
    const [graficaUltimaVisitaCliente, setGraficaUltimaVisitaCliente] = useState([]);
    const [visitasEjecutivos, setVisitasEjecutivos] = useState([]);
    const [visitasInplant, setVisitasInplant] = useState([]);
    const [visitasZona, setVisitasZona] = useState([]);

    const fmtFecha = (d) => d.toISOString().split('T')[0];

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [t, gc, ve, vi, vz, guvc] = await Promise.all([
                getVisitasTotales(fmtFecha(fechaInicio), fmtFecha(fechaFin)),
                getGraficaCliente(fmtFecha(fechaInicio), fmtFecha(fechaFin)),
                getVisitasEjecutivos(fmtFecha(fechaInicio), fmtFecha(fechaFin)),
                getVisitasInplant(fmtFecha(fechaInicio), fmtFecha(fechaFin)),
                getVisitasZona(fmtFecha(fechaInicio), fmtFecha(fechaFin)),
                getGraficaUltimaVisitaCliente(fmtFecha(fechaInicio), fmtFecha(fechaFin)),
            ]);
            setTotales(t?.visitasTotales ?? t);
            setGraficaCliente(gc?.graficaCliente ?? gc ?? []);
            setVisitasEjecutivos(ve?.visitasEjecutivos ?? ve ?? []);
            setVisitasInplant(vi?.visitasInplant ?? vi ?? []);
            setVisitasZona(vz?.visitasZona ?? vz ?? []);
            setGraficaUltimaVisitaCliente(guvc?.graficaUltimaVisitaCliente ?? guvc ?? []);
        } catch (error) {
            console.error('Error al cargar datos:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { cargarDatos(); }, []);

    const kpis = [
        { label: 'Total Visitas', key: 'TOTAL VISITAS', icon: 'pi pi-eye', color: '#4A90D9' },
        { label: 'Clientes Visitados', key: 'CLIENTES VISITADOS', icon: 'pi pi-users', color: '#5BC8AF' },
        { label: 'Clientes No Visitados', key: 'CLIENTES NO VISITADOS', icon: 'pi pi-user-minus', color: '#F59E42' },
        { label: 'Visitas Ejecutivo', key: 'VISITAS EJECUTIVO', icon: 'pi pi-briefcase', color: '#A78BFA' },
        { label: 'Visitas Inplant', key: 'VISITAS INPLANT', icon: 'pi pi-building', color: '#F87171' },
    ];

    const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        const RADIAN = Math.PI / 180;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        return percent > 0.04 ? (
            <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        ) : null;
    };

    const shortName = (v) => {
        const words = v?.trim().split(' ') ?? [];
        return words.length > 2 ? words.slice(0, 2).join(' ') + '…' : v?.trim();
    };



    const COLORS_ZONA = ['#4A90D9', '#5BC8AF', '#A78BFA', '#F59E42'];

    const KpiCard = ({ label, value, icon, color }) => (
        <Card className="rdv-kpi-card">
            <div className="rdv-kpi-inner">
                <div className="rdv-kpi-icon" style={{ background: color }}>
                    <i className={icon} style={{ fontSize: '1.4rem', color: '#fff' }} />
                </div>
                <div className="rdv-kpi-text">
                    <span className="rdv-kpi-value">{value ?? '—'}</span>
                    <span className="rdv-kpi-label">{label}</span>
                </div>
            </div>
        </Card>
    );

    const CustomTooltipHBar = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="rdv-tooltip">
                    <p className="rdv-tooltip-label">{label?.trim()}</p>
                    <p className="rdv-tooltip-value">{payload[0].value} visitas</p>
                </div>
            );
        }
        return null;
    };

    const datosConVisitas = graficaUltimaVisitaCliente
        .filter(d => d.TRANSCURRIDO !== null)
        .map(d => ({ ...d, CLIENTE: d.CLIENTE?.trim(), DIAS_ABS: Math.abs(d.TRANSCURRIDO) }))
        .sort((a, b) => b.DIAS_ABS - a.DIAS_ABS);

    const datosSinVisitas = graficaUltimaVisitaCliente
        .filter(d => d.TRANSCURRIDO === null)
        .map(d => ({ ...d, CLIENTE: d.CLIENTE?.trim() }));

    const visitasInplantAgrupadas = useMemo(() => {
        const mapa = {};
        visitasInplant.forEach(({ COLABORADOR, VISITAS, CLIENTE }) => {
            if (!mapa[COLABORADOR]) {
                mapa[COLABORADOR] = { COLABORADOR, VISITAS: 0, clientes: [] };
            }
            mapa[COLABORADOR].VISITAS += VISITAS;
            mapa[COLABORADOR].clientes.push({ CLIENTE: CLIENTE?.trim(), VISITAS });
        });
        return Object.values(mapa).sort((a, b) => b.VISITAS - a.VISITAS);
    }, [visitasInplant]);

    const CustomTooltipInplant = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const fila = visitasInplantAgrupadas.find(r => r.COLABORADOR === label);
        return (
            <div className="rdv-tooltip">
                <p className="rdv-tooltip-label" style={{ marginBottom: 6 }}>{label?.trim()}</p>
                <p className="rdv-tooltip-value" style={{ marginBottom: 6 }}>Total: <strong>{payload[0].value}</strong> visitas</p>
                {fila?.clientes.map((c, i) => (
                    <p key={i} style={{ margin: '2px 0', fontSize: 12 }}>
                        <span style={{ color: '#94a3b8' }}>•  <strong>{c.VISITAS}</strong> : </span> {c.CLIENTE}
                    </p>
                ))}
            </div>
        );
    };


    return (
        <div className="ReporteDeVisitas">

            {/* ── Encabezado / Filtros ── */}
            <div className="rdv-header">
                <div className="rdv-header-filters">
                    <div className="rdv-filter-group">
                        <label>Fecha inicio</label>
                        <Calendar
                            value={fechaInicio}
                            onChange={(e) => setFechaInicio(e.value)}
                            dateFormat="dd/mm/yy"
                            showIcon
                            maxDate={fechaFin}
                        />
                    </div>
                    <div className="rdv-filter-group">
                        <label>Fecha fin</label>
                        <Calendar
                            value={fechaFin}
                            onChange={(e) => setFechaFin(e.value)}
                            dateFormat="dd/mm/yy"
                            showIcon
                            minDate={fechaInicio}
                        />
                    </div>
                    <Button
                        label="Consultar"
                        icon="pi pi-search"
                        onClick={cargarDatos}
                        loading={loading}
                        className="rdv-btn-consultar"
                    />
                </div>
            </div>

            {loading ? (
                <div className="rdv-loading">
                    <ProgressSpinner />
                </div>
            ) : (
                <>
                    {/* ── KPIs ── */}
                    <div className="rdv-kpi-row">
                        {kpis.map((k) => (
                            <KpiCard
                                key={k.key}
                                label={k.label}
                                value={totales?.[k.key]}
                                icon={k.icon}
                                color={k.color}
                            />
                        ))}
                    </div>



                    {/* ── Fila inferior: Zona · Ejecutivos · Inplant ── */}
                    <div className="rdv-bottom-row">

                        {/* Donut — Zona Operativa */}
                        <Card className="rdv-card-bottom" title="Visitas por Zona Operativa">
                            <ResponsiveContainer width="100%" height={290}>
                                <PieChart>
                                    <Pie
                                        data={visitasZona}
                                        dataKey="VISITAS"
                                        nameKey="ZONA OPERATIVA"
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={65}
                                        outerRadius={110}
                                        labelLine={false}
                                        label={renderCustomLabel}
                                    >
                                        {visitasZona.map((_, i) => (
                                            <Cell key={i} fill={COLORS_ZONA[i % COLORS_ZONA.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(v, n) => [v, n]} />
                                    <Legend
                                        formatter={(value) => value?.trim()}
                                        iconType="circle"
                                        wrapperStyle={{ fontSize: 12 }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </Card>

                        {/* Barras — Ejecutivos */}
                        <Card className="rdv-card-bottom" title="Visitas por Ejecutivo">
                            <ResponsiveContainer width="100%" height={290}>
                                <BarChart
                                    data={visitasEjecutivos}
                                    layout="vertical"
                                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                                    <YAxis
                                        type="category"
                                        dataKey="COLABORADOR"
                                        width={145}
                                        tick={{ fontSize: 10 }}
                                        tickFormatter={shortName}
                                    />
                                    
                                    <Tooltip formatter={(v) => [v, 'Visitas']} labelFormatter={(l) => l?.trim()} />
                                    <Bar dataKey="VISITAS" fill="#A78BFA" radius={[0, 4, 4, 0]} barSize={20}>
                                        <LabelList dataKey="VISITAS" position="center" style={{ fill: '#fff', fontWeight: 700, fontSize: 11 }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>

                        {/* Barras — Inplant */}
                        <Card className="rdv-card-bottom" title="Visitas Inplant por Colaborador">
                            <ResponsiveContainer width="100%" height={290}>
                                <BarChart
                                    data={visitasInplantAgrupadas}
                                    layout="vertical"
                                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                    <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                                    <YAxis
                                        type="category"
                                        dataKey="COLABORADOR"
                                        width={145}
                                        tick={{ fontSize: 10 }}
                                        tickFormatter={shortName}
                                    />
                                    <Tooltip content={<CustomTooltipInplant />} />
                                    <Bar dataKey="VISITAS" fill="#5BC8AF" radius={[0, 4, 4, 0]} barSize={20}>
                                        <LabelList dataKey="VISITAS" position="center" style={{ fill: '#fff', fontWeight: 700, fontSize: 11 }} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>

                    </div>
                    {/* ── Gráfica Visitas por Cliente (horizontal) ── */}
                    <Card className="rdv-card-full" title="Visitas por Cliente">
                        <ResponsiveContainer width="100%" height={Math.max(320, graficaCliente.length * 28)}>
                            <BarChart
                                data={graficaCliente}
                                layout="vertical"
                                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                                responsive
                            >
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                                <YAxis
                                    type="category"
                                    dataKey="CLIENTE"
                                    width={205}
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(v) => v?.trim()}
                                />
                                <Tooltip content={<CustomTooltipHBar />} />
                                <Bar dataKey="VISITAS" fill="#4A90D9" radius={[0, 4, 4, 0]} barSize={14}>
                                    <LabelList dataKey="VISITAS" position="center" style={{ fill: '#fff', fontWeight: 700, fontSize: 11 }} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>

                    {/* ── Última Visita por Cliente ── */}
                    {graficaUltimaVisitaCliente.length > 0 && (
                        <Card className="rdv-card-full" title="Última visita por cliente en general">

                            {datosConVisitas.length > 0 && (
                                <DataTable
                                    value={datosConVisitas}
                                    stripedRows
                                    scrollable
                                    size="small"
                                    sortField="DIAS_ABS"
                                    sortOrder={-1}
                                    style={{ fontSize: '0.875rem' }}
                                >
                                    <Column field="CLIENTE" header="Cliente" sortable style={{ minWidth: '200px' }} />
                                    <Column field="ULTIMA VISITA" header="Última Visita" sortable style={{ minWidth: '130px' }} />
                                    <Column
                                        field="DIAS_ABS"
                                        header="Días transcurridos"
                                        sortable
                                        style={{ minWidth: '140px', textAlign: 'center' }}
                                        body={(row) => (
                                            <span style={{ fontWeight: 700, color: '#1e293b' }}>
                                                {row.DIAS_ABS === 0 ? 'Hoy' : `${row.DIAS_ABS} día${row.DIAS_ABS !== 1 ? 's' : ''}`}
                                            </span>
                                        )}
                                    />
                                </DataTable>
                            )}

                            {/* Sin visitas */}
                            {datosSinVisitas.length > 0 && (
                                <div className="rdv-uv-sinvisitas">
                                    <span className="rdv-uv-sinvisitas-title">
                                        <i className="pi pi-exclamation-circle" />
                                        Sin visitas registradas ({datosSinVisitas.length})
                                    </span>
                                    <div className="rdv-uv-chips">
                                        {datosSinVisitas.map((d, i) => (
                                            <span key={i} className="rdv-uv-chip">{d.CLIENTE}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>
                    )}
                </>
            )}
        </div>
    );
};

export default ReporteDeVisitas;